<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { Link, page } from '@inertiajs/svelte';
    import CaseCard from '../components/CaseCard.svelte';
    import { show } from '@/routes/cases';
    import { formatDate as fmtDate } from '../utils/dates';

    interface CaseSummary {
        id: number;
        sqid: string;
        date: string;
        history_excerpt: string;
        institution?: string;
        computed_status: string;
        topic_name?: string;
        image_url: string | null;
        image_srcset?: { url: string; width: number }[];
        answer_status?: string | null;
    }

    interface PaginatedCases {
        data: CaseSummary[];
        current_page: number;
        last_page: number;
        links: { url: string | null; label: string; active: boolean }[];
    }

    let { cases }: { cases: PaginatedCases } = $props();

    type Filter = 'all' | 'answered' | 'unanswered';
    let filter = $state<Filter>('all');

    const isLoggedIn = $derived(!!$page?.props?.auth?.user);

    const filteredData = $derived(
        filter === 'all'
            ? cases.data
            : filter === 'answered'
                ? cases.data.filter(c => c.answer_status && c.answer_status !== 'unanswered')
                : cases.data.filter(c => c.answer_status === 'unanswered')
    );

    function formatDate(dateStr: string): string {
        return fmtDate(dateStr, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }

    const groupedByDate = $derived(() => {
        const groups: { date: string; label: string; cases: CaseSummary[] }[] = [];
        for (const c of filteredData) {
            const last = groups[groups.length - 1];
            if (last && last.date === c.date) {
                last.cases.push(c);
            } else {
                groups.push({ date: c.date, label: formatDate(c.date), cases: [c] });
            }
        }
        return groups;
    });
</script>

<svelte:head>
    <title>All Cases — Case of the Day</title>
</svelte:head>

<div class="max-w-7xl mx-auto">
        <div class="flex items-center justify-between mb-6">
            <h1 class="text-2xl font-bold text-gray-100">All Cases</h1>
            {#if isLoggedIn}
                <div class="flex gap-1">
                    {#each [['all', 'All'], ['answered', 'Answered'], ['unanswered', 'Unanswered']] as [value, label] (value)}
                        <button
                            type="button"
                            onclick={() => filter = value as Filter}
                            class="px-3 py-1 text-sm rounded-full transition-colors {filter === value ? 'bg-cyan-600 text-white font-medium' : 'text-gray-400 hover:bg-gray-800'}"
                        >
                            {label}
                        </button>
                    {/each}
                </div>
            {/if}
        </div>

        {#if cases.data.length === 0}
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 text-center">
                <p class="text-gray-500">No cases available yet. Check back later!</p>
            </div>
        {:else if groupedByDate().length === 0}
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 text-center">
                <p class="text-gray-500">No {filter === 'answered' ? 'answered' : 'unanswered'} cases on this page.</p>
            </div>
        {:else}
            <div class="space-y-8">
                {#each groupedByDate() as group (group.date)}
                    <section>
                        <h2 class="text-lg font-semibold text-gray-300 mb-4">{group.label}</h2>
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {#each group.cases as c (c.id)}
                                {@const [year, month, day] = c.date.split('-')}
                                <Link href={show({ year: +year, month: +month, day: +day, sqid: c.sqid })} class="group bg-gray-900 rounded-lg shadow border border-gray-700 hover:border-gray-600 hover:shadow-md transition-all overflow-hidden flex flex-col">
                                    <CaseCard
                                        imageUrl={c.image_url}
                                        imageSrcset={c.image_srcset}
                                        topicName={c.topic_name}
                                        historyExcerpt={c.history_excerpt}
                                        institution={c.institution}
                                        answerStatus={c.answer_status}
                                        hoverZoom
                                    />
                                </Link>
                            {/each}
                        </div>
                    </section>
                {/each}
            </div>

            {#if cases.last_page > 1}
                <nav class="flex justify-center mt-8 gap-1" aria-label="Pagination">
                    {#each cases.links as link (link.label)}
                        {#if link.url}
                            <Link
                                href={link.url}
                                class="px-3 py-2 text-sm rounded-md transition-colors {link.active ? 'bg-cyan-600 text-white font-medium' : 'text-gray-400 hover:bg-gray-800'}"
                            >
                                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                                {@html link.label}
                            </Link>
                        {:else}
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            <span class="px-3 py-2 text-sm text-gray-600">{@html link.label}</span>
                        {/if}
                    {/each}
                </nav>
            {/if}
        {/if}
    </div>
